import "./style.scss";

import { View } from "@amzn/stencil-react-components/layout";
import {
  ProgressTracker,
  ProgressTrackerLayout,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
} from "@amzn/stencil-react-components/progress-tracker";
import {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";

import { AiAutomationChip } from "@/components/AIAutomation/AiAutomationChip";
import {
  progressRailCompactClassName,
  Step,
  STEP_ID,
} from "@/components/constants";
import { getStepEstimatedMinutes } from "@/helpers/step-time-estimates";
import { useIsJourneyCaptureAvailable } from "@/hooks/useIsJourneyCaptureAvailable";
import { AppContext } from "@/stores/appStore";
import { getEnabledSteps } from "@/stores/constants";
import { useFeatureFlagsContext } from "@/stores/contexts/FeatureFlagsContext";

/**
 * `ProgressTracker` requires an `onStepSelected` handler. The rail is rendered with
 * `isStatic`, which strips the click and keyboard handlers, so this is never called.
 */
const noop = (): void => undefined;

/**
 * Message ids for the step labels shown on the rail.
 *
 * These are deliberately separate from each step's `titleId`, which is the heading of the
 * step content itself. A rail label is written to stay on one or two lines in a narrow
 * column, for example "Identity Verification" on the rail against "Verify your identity"
 * as the step heading.
 *
 * Amazon Account Caching is the exception and reuses its `titleId`. That heading is already
 * the same short noun phrase a rail label would be, so a dedicated resource would only
 * duplicate it and need translating separately.
 */
const RAIL_STEP_LABEL_IDS: Record<STEP_ID, string> = {
  [STEP_ID.IDENTITY_CHECK]: "progressTracker-step-identityCheck",
  [STEP_ID.AMAZON_USERNAME]: "progressTracker-step-amazonUsername",
  [STEP_ID.INSERT_AND_TEST_SECURITY_KEY]:
    "progressTracker-step-insertAndTestSecurityKey",
  [STEP_ID.REGISTER_SECURITY_KEY]: "progressTracker-step-registerSecurityKey",
  [STEP_ID.CONNECT_VPN]: "progressTracker-step-connectVpn",
  [STEP_ID.AMAZON_PASSWORD]: "progressTracker-step-amazonPassword",
  [STEP_ID.PRE_CACHE_AD_MOBILE]: "progressTracker-step-preCacheADMobile",
  [STEP_ID.AMAZON_ACCOUNT_CACHING]: "amazonAccountCaching-expander-title",
};

/**
 * The one step Aiva can drive today.
 *
 * A single id rather than a field on `Step`: the capability belongs to the automation, not to the
 * step, and a second automatable step should add the field rather than extend this constant.
 */
const AI_AUTOMATION_STEP_ID = STEP_ID.CONNECT_VPN;

/**
 * How far below the step label the chip sits, from the design. `$ai-chip-row-gap` in style.scss
 * reserves the gap it drops into.
 */
const CHIP_OFFSET_BELOW_LABEL = 3;

/**
 * Measures how far below the rail anchor's top the chip sits for the step at `automatableIndex`, or
 * `undefined` when the walk cannot find it. A Stencil rename of a `data-test-component` lands there,
 * and the caller then drops the chip rather than pinning it to the anchor's corner.
 *
 * Vertical only, because row heights vary with the label wrap. style.scss owns the inline axis.
 *
 * Rects, not `offsetTop`: that is measured from the step `li`, which Stencil positions `relative` in
 * the vertical layout, so it would need a walk up the offsetParent chain. Both rects come from one
 * frame, so the scroll of the column above cancels.
 *
 * @param anchor The rail anchor holding the tracker, or null before it mounts.
 * @param automatableIndex Index of the step to measure, among the steps the rail rendered.
 */
export const measureChipTop = (
  anchor: HTMLElement | null,
  automatableIndex: number
): number | undefined => {
  const row = anchor?.querySelectorAll<HTMLElement>(
    'li[data-test-component="StencilProgressTrackerStep"]'
  )[automatableIndex];
  const stepWrapper = row?.querySelector<HTMLElement>(
    '[data-test-component="ProgressTrackerStepWrapper"]'
  );
  if (!anchor || !stepWrapper) {
    return undefined;
  }

  return (
    stepWrapper.getBoundingClientRect().bottom -
    anchor.getBoundingClientRect().top +
    CHIP_OFFSET_BELOW_LABEL
  );
};

/**
 * Maps a step onto the status the tracker renders it with.
 *
 * Upcoming steps use `Available` rather than `Disabled` so screen readers announce them
 * as "incomplete" instead of "disabled" — the wizard is linear, so a later step is not
 * unavailable, just not reached yet.
 *
 * @param step The step to map.
 * @param index The index of the step within the enabled steps.
 * @param activeIndex The index of the step the new hire is currently on.
 */
export const getStepStatus = (
  step: Step,
  index: number,
  activeIndex: number
): ProgressTrackerStepStatus => {
  if (step.completed) {
    return ProgressTrackerStepStatus.Complete;
  }
  if (index === activeIndex) {
    return ProgressTrackerStepStatus.Current;
  }
  return ProgressTrackerStepStatus.Available;
};

/**
 * The vertical progress rail shown in the left column of the Day1 onboarding page.
 *
 * Lists every enabled onboarding step with its title and estimated completion time,
 * marking each one complete, current, or upcoming. The rail is informational only —
 * steps are not clickable, because the wizard advances strictly forwards.
 */
export default function ProgressRail(): JSX.Element {
  const intl = useIntl();
  const { state } = useContext(AppContext);
  const { featureFlags } = useFeatureFlagsContext();
  const isJourneyCaptureAvailable = useIsJourneyCaptureAvailable();
  /*
   * Filtered with the same feature flags HomeContent uses. Some steps are gated on a
   * flag, so filtering without them would give the rail a shorter list than the content
   * and the two would disagree on which step currentStepNum refers to.
   */
  const steps = getEnabledSteps(state.steps, featureFlags);
  // currentStepNum is 1 based; it sits past the last step once everything is complete.
  const activeIndex = state.currentStepNum - 1;

  /**
   * An open flyout squeezes the page, so the rail drops its step labels and estimated
   * times to leave the step content a usable width. Opening Get Support also opens the
   * chat support flyout (see the SET_GET_SUPPORT_FLYOUT_VISIBLE reducer case), and the
   * chatbot occupies the same space, so every flyout that overlays the page collapses
   * the rail.
   */
  const isCompact =
    state.chatSupportFlyoutVisible ||
    state.chatbotFlyoutVisible ||
    state.getSupportFlyoutVisible;

  const automatableIndex = steps.findIndex(
    (step) => step.id === AI_AUTOMATION_STEP_ID
  );
  /* Withheld while compact: the labels are clipped there, so a floating chip would sit on nothing. */
  const shouldMarkAutomatableStep =
    isJourneyCaptureAvailable && !isCompact && automatableIndex >= 0;

  const anchorRef = useRef<HTMLDivElement>(null);
  const [chipTop, setChipTop] = useState<number>();
  /* The feature flags arrive after the first render, so narrowing these dependencies loses the chip. */
  const updateChipTop = useCallback((): void => {
    setChipTop(
      shouldMarkAutomatableStep
        ? measureChipTop(anchorRef.current, automatableIndex)
        : undefined
    );
  }, [shouldMarkAutomatableStep, automatableIndex]);

  useLayoutEffect(() => {
    updateChipTop();
    window.addEventListener("resize", updateChipTop);
    return () => window.removeEventListener("resize", updateChipTop);
  }, [updateChipTop, activeIndex, steps.length]);

  return (
    <View
      className={isCompact ? progressRailCompactClassName : undefined}
      dataTestId="progressRail"
    >
      <div
        className="ezo-progress-rail-anchor"
        /* Publishes the row ordinal so style.scss can widen that row's gap. ProgressTrackerStep
           takes no className, so there is nothing else to hang the rule on. Tied to the same
           measurement the chip is, so a failed measurement cannot leave a widened row with no chip
           in it. The gap sits on a later sibling, so reserving it cannot move what was measured. */
        data-ai-automation-step={
          chipTop === undefined ? undefined : automatableIndex + 1
        }
        data-test-id="progressRailAnchor"
        ref={anchorRef}
      >
        <ProgressTracker
          aria-label={intl.formatMessage({ id: "progressTracker-ariaLabel" })}
          isStatic={true}
          layout={ProgressTrackerLayout.Vertical}
          onStepSelected={noop}
          selectedStep={activeIndex}
        >
          {steps.map((step, index) => (
            <ProgressTrackerStep
              /*
               * Omitted while compact so the estimated time is not rendered at all. The
               * label has to stay, since it is what the step announces to screen readers,
               * so it is hidden visually in CSS instead.
               */
              descriptionText={
                isCompact
                  ? undefined
                  : intl.formatMessage(
                      { id: "progressTracker-step-estimatedTime" },
                      { minutes: getStepEstimatedMinutes(step.id) }
                    )
              }
              key={step.id}
              labelText={intl.formatMessage({
                id: RAIL_STEP_LABEL_IDS[step.id],
              })}
              status={getStepStatus(step, index, activeIndex)}
            />
          ))}
        </ProgressTracker>
        {chipTop !== undefined && (
          <div
            className="ezo-progress-rail-ai-chip"
            data-test-id="progressRailAiChipSlot"
            style={{ top: chipTop }}
          >
            {/* Decorative here: the chip cannot nest inside the step it marks, so the fact is
                announced by the hidden line below instead of twice. */}
            <AiAutomationChip
              testIdPrefix="progressRailAiAutomation"
              isDecorative
            />
            <span className="ezo-progress-rail-sr-only">
              {`${intl.formatMessage({
                id: RAIL_STEP_LABEL_IDS[AI_AUTOMATION_STEP_ID],
              })}: ${intl.formatMessage({
                id: "aiConsent-ai-automation-chip",
              })}`}
            </span>
          </div>
        )}
      </div>
    </View>
  );
}
